/*
 * @Author: Ryan
 * @Date: 2026-02-03
 * @Description: Showcase section - Turn Every Photo Into a Masterpiece with FalcoCut
 */
import Image from 'next/image';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { scrollToTop } from '@/utils/landing/scrollToTop';

interface ShowcaseImage {
  id: string;
  beforeUrl: string;
  afterUrl: string;
}

const showcaseImages: ShowcaseImage[] = [
  {
    id: 'headphones',
    beforeUrl:
      'https://assets.falcocut.ai/ai-image-enhancer/headphones-image-before.webp',
    afterUrl: 'https://assets.falcocut.ai/ai-image-enhancer/headphones-image-after.webp',
  },
  {
    id: 'ugc-skincare',
    beforeUrl:
      'https://assets.falcocut.ai/ai-image-enhancer/ugc-skincare-product-image-before.webp',
    afterUrl:
      'https://assets.falcocut.ai/ai-image-enhancer/ugc-skincare-product-image-after.webp',
  },
  {
    id: 'urban-night',
    beforeUrl:
      'https://assets.falcocut.ai/ai-image-enhancer/urban-night-image-before.webp',
    afterUrl: 'https://assets.falcocut.ai/ai-image-enhancer/urban-night-image-after.webp',
  },
  {
    id: 'outdoor-vlog',
    beforeUrl:
      'https://assets.falcocut.ai/ai-image-enhancer/outdoor-vlog-image-before.webp',
    afterUrl:
      'https://assets.falcocut.ai/ai-image-enhancer/outdoor-vlog-image-after.webp',
  },
  {
    id: 'cosmetic-products',
    beforeUrl:
      'https://assets.falcocut.ai/ai-image-enhancer/cosmetic-products-image-before.webp',
    afterUrl:
      'https://assets.falcocut.ai/ai-image-enhancer/cosmetic-products-image-after.webp',
  },
  {
    id: 'skin-care',
    beforeUrl: 'https://assets.falcocut.ai/ai-image-enhancer/skin-care-image-before.webp',
    afterUrl: 'https://assets.falcocut.ai/ai-image-enhancer/skin-care-image-after.webp',
  },
];

// 拖拽手柄内的左箭头图标
const SliderLeftArrowIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="12"
    viewBox="0 0 12 16"
    fill="none"
  >
    <path d="M0 8L12 0V16L0 8Z" fill="black" />
    <path d="M0 8L12 0V16L0 8Z" fill="black" fillOpacity="0.2" />
  </svg>
);

// 拖拽手柄内的右箭头图标
const SliderRightArrowIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="12"
    viewBox="0 0 12 16"
    fill="none"
  >
    <path d="M12 8L0 0V16L12 8Z" fill="black" />
  </svg>
);

// 箭头图标
const ArrowRightIcon: React.FC = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" width="1em" height="1em">
    <path d="M869 487.8L491.2 159.9c-2.9-2.5-6.6-3.9-10.5-3.9h-88.5c-7.4 0-10.8 9.2-5.2 14l350.2 304H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h585.1L386.9 854c-5.6 4.9-2.2 14 5.2 14h91.5c1.9 0 3.8-.7 5.2-2L869 536.2a32.07 32.07 0 000-48.4z" />
  </svg>
);

// 单个图片对比卡片组件
interface CompareCardProps {
  image: ShowcaseImage;
}

const CompareCard: React.FC<CompareCardProps> = ({ image }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMove = useCallback(
    (clientX: number) => {
      if (!containerRef.current || !isDragging) return;

      const rect = containerRef.current.getBoundingClientRect();
      const x = clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      setSliderPosition(percentage);
    },
    [isDragging],
  );

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      handleMove(e.clientX);
    },
    [handleMove],
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (e.touches.length > 0) {
        handleMove(e.touches[0].clientX);
      }
    },
    [handleMove],
  );

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove]);

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden rounded-2xl bg-gray-100 select-none"
      style={{ aspectRatio: '4/3' }}
    >
      {/* After 图片 (底层) */}
      <Image
        src={image.afterUrl}
        alt="After enhancement"
        fill
        className="object-cover"
        sizes="(max-width: 768px) 100vw, 33vw"
        draggable={false}
      />

      {/* Before 图片 (上层，通过 clip 裁切) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
      >
        <Image
          src={image.beforeUrl}
          alt="Before enhancement"
          fill
          className="object-cover"
          sizes="(max-width: 768px) 100vw, 33vw"
          draggable={false}
        />
      </div>

      {/* 拖拽分割线 */}
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white cursor-ew-resize z-10"
        style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
      >
        {/* 拖拽手柄 */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize"
          style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
        >
          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center gap-1">
            <SliderLeftArrowIcon />
            <SliderRightArrowIcon />
          </div>
        </div>
      </div>
    </div>
  );
};

const ShowcaseSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Turn Every Photo Into a Masterpiece with FalcoCut
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-5xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          Watch how FalcoCut transforms different types of photos with ease—from dark
          shots and old memories to anime art, low-res images, grainy textures, and blurry
          portraits. In just seconds, your pictures become sharper, clearer, and ready to
          share at stunning quality.
        </p>

        {/* Image Grid */}
        <div className="grid grid-cols-1 pad:grid-cols-2 sm:grid-cols-3 gap-4 pad:gap-6 mb-10 pad:mb-14">
          {showcaseImages.map((image) => (
            <CompareCard key={image.id} image={image} />
          ))}
        </div>

        {/* CTA Button */}
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => scrollToTop()}
            className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-white rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
            style={{
              background: 'linear-gradient(90deg, #4D2FFF 0%, #EF5CFF 100%)',
            }}
          >
            <span>Enhance Your Image</span>
            <ArrowRightIcon />
          </button>
        </div>
      </div>
    </section>
  );
};

export default ShowcaseSection;
